import type { CommentHubNotificationPrefs } from 'shared-code/commenthub-api'

/**
 * The reader's one email question (notifications design 1b, 2026-10-28): "Email me about" —
 * Replies and questions / All activity / Account notices only. Nothing is stored as the choice;
 * it is READ from commentHub's `emailTypes` (and, on SoundGuys, written to DealHunt's two owner
 * email keys as well), and picking an option writes the whole list in one PUT.
 *
 * The three kind families are the boundaries commentHub's own default draws (every emailable kind
 * but `comment_liked` and `thread_reply`): a conversation kind, a recognition kind, an account
 * notice. A kind commentHub adds later lands in "All activity" until it is placed here.
 */

export type EmailChoice = 'replies' | 'all' | 'account'

/** The settings anchor on an account Notifications page — the bell's "Settings" lands on it. */
export const NOTIFY_SETTINGS_ANCHOR = 'notify-settings'

/** Replies to you, new comments where you joined — the recommended choice. */
export const CONVERSATION_KINDS: readonly string[] = ['reply', 'thread_reply', 'thread_followed']
/** Likes and pinned (the server's `comment_featured` is a legacy editor pick: pinned too). */
export const RECOGNITION_KINDS: readonly string[] = [
  'comment_liked',
  'comment_featured',
  'comment_pinned',
]
/** Removed comments, role changes — emailed under every choice. */
export const ACCOUNT_KINDS: readonly string[] = ['comment_removed', 'role_changed']

export interface EmailChoiceOption {
  id: EmailChoice
  label: string
  /** The shared description; a brand may replace it (SoundGuys names its owner-review emails). */
  detail: string
}

export const EMAIL_CHOICES: readonly EmailChoiceOption[] = [
  {
    id: 'replies',
    label: 'Replies and conversations',
    detail: 'Recommended. Replies to you, new comments where you joined.',
  },
  { id: 'all', label: 'All activity', detail: 'Also likes and pinned comments.' },
  { id: 'account', label: 'Account notices only', detail: 'Pauses everything else.' },
]

/** commentHub's batching (its runbook `docs/reader-notification-email.md`): one mail per reader per
 *  hour, after ten quiet minutes, listing every new group. */
export const EMAIL_CADENCE_LINE = "One email at most per hour, grouping what's new."

/**
 * Whether this commentHub sends notification email: its preferences carry `emailAvailableTypes`.
 * An older server omits the email fields — the box then says email is not available yet.
 */
export function commentEmailLive(prefs: CommentHubNotificationPrefs | null | undefined): boolean {
  return Array.isArray(prefs?.emailAvailableTypes)
}

/** "Email goes to … Never marketing." + the cadence: the footnote once email is live. */
export function emailFootnoteLine(emailMasked: string | null | undefined, cadence: string): string {
  return [emailMasked && `Email goes to ${emailMasked}.`, 'Never marketing.', cadence]
    .filter(Boolean)
    .join(' ')
}

const some = (list: readonly string[], kinds: readonly string[]): boolean =>
  list.some(t => kinds.includes(t))

/**
 * The choice the stored `emailTypes` amount to: any recognition kind (or a kind outside the three
 * families) → All activity; else any conversation kind → Replies; else Account notices only. A
 * mixed state (say a hand-set list) reads as the higher choice and is normalised on the next pick.
 * Null while email is not live or the prefs have not loaded.
 */
export function emailChoiceOf(
  prefs: CommentHubNotificationPrefs | null | undefined
): EmailChoice | null {
  if (!prefs || !commentEmailLive(prefs)) {
    return null
  }
  const types = prefs.emailTypes ?? []
  const other = types.filter(t => !CONVERSATION_KINDS.includes(t) && !ACCOUNT_KINDS.includes(t))
  if (other.length > 0 || some(types, RECOGNITION_KINDS)) {
    return 'all'
  }
  return some(types, CONVERSATION_KINDS) ? 'replies' : 'account'
}

/** The whole `emailTypes` list a choice writes, out of what this commentHub can email. */
export function emailTypesFor(choice: EmailChoice, available: readonly string[]): string[] {
  switch (choice) {
    case 'all':
      return [...available]
    case 'replies':
      return available.filter(t => CONVERSATION_KINDS.includes(t) || ACCOUNT_KINDS.includes(t))
    default:
      return available.filter(t => ACCOUNT_KINDS.includes(t))
  }
}

const RANK: Record<EmailChoice, number> = { account: 0, replies: 1, all: 2 }

/**
 * The higher of two readings — a SoundGuys owner's choice lives in two stores (commentHub's
 * list, DealHunt's two keys) that My gear can still move apart; the box shows the higher, and a
 * pick (even of the shown one) writes both back in line.
 */
export function higherChoice(a: EmailChoice | null, b: EmailChoice | null): EmailChoice | null {
  if (a === null) {
    return b
  }
  if (b === null) {
    return a
  }
  return RANK[a] >= RANK[b] ? a : b
}
